import React, { useState, useEffect, useCallback } from "react";
import { Card } from "../ui/Card";
import { Button } from "../ui/Button";
import { Heading } from "../ui/Heading";
import { productCustomizationApi } from "../../services/productCustomizationApi";
import ThreeDDesignCanvas, { ThreeDDesignCanvasRef } from "./ThreeDDesignCanvas";
import type { DesignElement } from "./types";
import { useAuth } from "../../context/AuthContext";

export interface ThreeDModelViewerProps {
  customizationId: string;
  modelUrl?: string; // Reserved for future use
  previewUrl?: string;
  onClose: () => void;
}

export const ThreeDModelViewer: React.FC<ThreeDModelViewerProps> = ({
  customizationId,
  modelUrl: _modelUrl, // Reserved for future use
  previewUrl,
  onClose,
}) => {
  const [preview, setPreview] = useState<string | undefined>(previewUrl);
  const [loading, setLoading] = useState(!previewUrl);
  const [exportingSTL, setExportingSTL] = useState(false);
  const [exportingOBJ, setExportingOBJ] = useState(false);
  const [customizationData, setCustomizationData] = useState<any>(null);
  const [useCanvasExport, setUseCanvasExport] = useState(false);
  const canvasRef = React.useRef<ThreeDDesignCanvasRef>(null);
  const { user } = useAuth();
  
  // Check if user is admin, store_admin, or co_admin
  const isAdmin = user?.role === "admin" || user?.role === "store_admin" || user?.role === "co_admin";

  const loadCustomization = useCallback(async () => {
    try {
      setLoading(true);
      const customization = await productCustomizationApi.getCustomizationById(customizationId);
      setCustomizationData(customization);
      
      // Always use 3D canvas view for new format (has elements)
      // For legacy format (with modelFileUrl), fall back to preview image
      if (customization.customizationType === "3d-print") {
        if (customization.elements && customization.elements.length > 0) {
          // New format - always use interactive 3D canvas
          setUseCanvasExport(true);
        } else if (customization.modelFileUrl) {
          // Legacy format - use preview image (no interactive view available)
          if (customization.modelPreviewImage) {
            setPreview(customization.modelPreviewImage);
          } else {
            // Try to load from backend endpoint
            try {
              const response = await productCustomizationApi.getModelPreview(customizationId);
              setPreview(response.previewUrl);
            } catch (error: any) {
              console.error("Error loading preview:", error);
            }
          }
        } else {
          // No elements and no modelFileUrl - show error or fallback
          console.warn("3D customization has no elements or model file");
        }
      }
    } catch (error: any) {
      console.error("Error loading customization:", error);
    } finally {
      setLoading(false);
    }
  }, [customizationId]);

  useEffect(() => {
    // Always load customization data to show 3D view
    loadCustomization();
  }, [loadCustomization]);

  const handleExportSTL = async () => {
    try {
      setExportingSTL(true);
      
      if (useCanvasExport && canvasRef.current) {
        // New format - export from canvas
        await canvasRef.current.export3DModel();
      } else {
        // Legacy format - export from backend
        const blob = await productCustomizationApi.exportSTL(customizationId);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `customization-${customizationId}.stl`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error: any) {
      console.error("Error exporting STL:", error);
      alert(`Failed to export STL: ${error.message}`);
    } finally {
      setExportingSTL(false);
    }
  };

  const handleExportOBJ = async () => {
    try {
      setExportingOBJ(true);
      
      if (useCanvasExport && canvasRef.current) {
        // New format - export from canvas
        await canvasRef.current.export3DModelOBJ();
      } else if (customizationData?.modelFileUrl) {
        // Legacy format - export from backend
        const blob = await productCustomizationApi.exportOBJ(customizationId);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `customization-${customizationId}.obj`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        // No model file available - try canvas export as fallback
        if (canvasRef.current && customizationData?.elements?.length > 0) {
          await canvasRef.current.export3DModelOBJ();
        } else {
          throw new Error("3D model not available for export. Please ensure the customization has been saved with a valid 3D model.");
        }
      }
    } catch (error: any) {
      console.error("Error exporting OBJ:", error);
      if (error.response?.status === 404) {
        alert("OBJ file not found. This customization may not have a 3D model file. Please try exporting as STL or recreate the customization.");
      } else {
        alert(`Failed to export OBJ: ${error.message}`);
      }
    } finally {
      setExportingOBJ(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-4xl w-full max-h-[90vh] overflow-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <Heading size="lg">3D Model Preview</Heading>
            <Button variant="ghost" onClick={onClose}>×</Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">Loading preview...</div>
            </div>
          ) : useCanvasExport && customizationData ? (
            <div className="mb-4">
              <div className="border border-gray-300 rounded overflow-hidden bg-gray-100" style={{ height: "500px", width: "100%" }}>
                <ThreeDDesignCanvas
                  ref={canvasRef}
                  backgroundColor={customizationData.backgroundColor || "#FFFFFF"}
                  backgroundDepth={customizationData.threeDData?.backgroundDepth || 5}
                  backgroundForm={customizationData.threeDData?.backgroundForm || "auto"}
                  backgroundEnabled={customizationData.threeDData?.enabled !== false}
                  backgroundWidth={customizationData.threeDData?.width || 100}
                  backgroundHeight={customizationData.threeDData?.height || 60}
                  backgroundBorderRadius={customizationData.threeDData?.borderRadius || 0}
                  keychainRingEnabled={customizationData.threeDData?.keychainRingEnabled || false}
                  keychainRingSide={customizationData.threeDData?.keychainRingSide || "top"}
                  elements={(customizationData.elements || []) as DesignElement[]}
                  selectedElement={null}
                  selectedCanvas={false}
                  onElementSelect={() => {}}
                  onCanvasSelect={() => {}}
                  onElementUpdate={() => {}}
                  onElementDelete={() => {}}
                  onElementDoubleClick={() => {}}
                />
              </div>
            </div>
          ) : preview ? (
            <div className="mb-4">
              <div className="border border-gray-300 rounded overflow-hidden bg-gray-100 flex items-center justify-center h-64">
                <img
                  src={preview}
                  alt="3D Model Preview"
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              Preview not available
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            {isAdmin && (
              <>
                <Button
                  variant="outline"
                  onClick={handleExportSTL}
                  disabled={exportingSTL}
                >
                  {exportingSTL ? "Exporting..." : "Export STL"}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleExportOBJ}
                  disabled={exportingOBJ}
                >
                  {exportingOBJ ? "Exporting..." : "Export OBJ"}
                </Button>
              </>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ThreeDModelViewer;

